import { useMutation, useQueryClient } from '@tanstack/react-query';
import { chatApi } from '../api';
import { SyncChatResultParams } from '../api/openapi';
import {
  ChatAdjustmentCTA,
  SessionMessage,
  StreamChatSessionType,
  SuggestedTaskItem,
} from '@demind-inc/core';

interface UseSyncChatResultParams {
  sessionId: string;
  sessionType: StreamChatSessionType;
  resultJson: string;
  userId: string;
  metricId: string;
  routineId?: string;
  calendarId?: string;
  dateToBeSuggested?: string;
}

export const useSyncChatResult = () => {
  const queryClient = useQueryClient();

  const syncChatResultMutation = useMutation({
    mutationFn: ({
      sessionId,
      sessionType,
      resultJson,
      routineId,
      userId,
      metricId,
      calendarId,
      dateToBeSuggested,
    }: UseSyncChatResultParams) => {
      const params: SyncChatResultParams = {
        sessionType,
        resultJson,
        userId,
        metricId,
        routineId,
        calendarId,
        dateToBeSuggested,
      };

      return chatApi.syncChatResult(sessionId, params).then(({ data }) => {
        return {
          ...data,
          sessions: (data.sessions ?? []) as SessionMessage[],
          suggestedTasks: (data.suggestedTasks ?? []) as SuggestedTaskItem[],
          cta: data.cta as ChatAdjustmentCTA | undefined,
        };
      });
    },
    onSuccess: (_, { sessionType }) => {
      // Invalidate calendar events cache for sleep and meal types
      if (['sleep', 'meal'].includes(sessionType)) {
        queryClient.invalidateQueries({ queryKey: ['lifestack.calendar.events'] });
      }
    },
  });

  return {
    syncChatResult: syncChatResultMutation.mutateAsync,
    ...syncChatResultMutation,
  };
};
